import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Shield, Lock, Eye, UserCheck, Mail, Phone } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';

const PrivacyPage = () => {
  const { formatPrice } = useCurrency();
  
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0E163B] to-[#1a2654] text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Your privacy matters to us. This policy explains how we collect, use, and protect your personal information.
          </p>
          <p className="text-lg text-gray-400 mb-6">
            Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
          <Badge className="bg-[#FF5A3D] text-white px-4 py-2">
            GDPR Compliant
          </Badge>
        </div>
      </section>

      {/* Privacy Highlights */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Your Privacy, Our Commitment
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We believe in transparency about how we handle your data. Here's what you need to know.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <Shield size={48} className="text-[#FF5A3D] mx-auto mb-4" />
                <h3 className="font-semibold text-[#0E163B] mb-2">Data Protection</h3>
                <p className="text-sm text-gray-600">Bank-level encryption for all your information</p>
              </CardContent>
            </Card>
            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <Lock size={48} className="text-[#FF5A3D] mx-auto mb-4" />
                <h3 className="font-semibold text-[#0E163B] mb-2">Secure Storage</h3>
                <p className="text-sm text-gray-600">Your data is stored securely in UK data centres</p>
              </CardContent>
            </Card>
            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <Eye size={48} className="text-[#FF5A3D] mx-auto mb-4" />
                <h3 className="font-semibner text-[#0E163B] mb-2">Full Transparency</h3>
                <p className="text-sm text-gray-600">Clear information about what we collect and why</p>
              </CardContent>
            </Card>
            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <UserCheck size={48} className="text-[#FF5A3D] mx-auto mb-4" />
                <h3 className="font-semibold text-[#0E163B] mb-2">Your Control</h3>
                <p className="text-sm text-gray-600">Full control over your data and privacy settings</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-12">
              <h2 className="text-xl font-semibold text-blue-900 mb-3">Quick Summary</h2>
              <p className="text-blue-800 text-sm">
                We collect only the information necessary to provide our service. We never sell your data to third parties. 
                You have full control over your information and can delete your account at any time.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">1. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-[#0E163B] mt-8 mb-4">Information You Provide</h3>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li><strong>Account Information:</strong> Name, email address, phone number, business name</li>
              <li><strong>Billing Information:</strong> Payment details (processed securely by our payment processor)</li>
              <li><strong>Content:</strong> Data you upload, create, or store using our service</li>
              <li><strong>Communications:</strong> Messages you send to our support team</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0E163B] mt-8 mb-4">Information We Automatically Collect</h3>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li><strong>Usage Data:</strong> How you interact with our service (features used, time spent)</li>
              <li><strong>Technical Data:</strong> IP address, browser type, device information</li>
              <li><strong>Performance Data:</strong> Service performance metrics and error logs</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">2. How We Use Your Information</h2>
            <p className="text-gray-600 mb-4">We use your information to:</p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li>Provide and maintain our service</li>
              <li>Process payments and manage your account</li>
              <li>Send important service updates and notifications</li>
              <li>Provide customer support</li>
              <li>Improve our service and develop new features</li>
              <li>Ensure security and prevent fraud</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">3. Information Sharing and Disclosure</h2>
            <p className="text-gray-600 mb-4">We do not sell, trade, or rent your personal information to third parties. We may share your information only in these limited circumstances:</p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li><strong>Service Providers:</strong> Trusted partners who help us operate our service (e.g., payment processors, hosting providers)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets (with notice to you)</li>
              <li><strong>Your Consent:</strong> When you explicitly agree to share information</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">4. Data Security</h2>
            <p className="text-gray-600 mb-4">We implement industry-standard security measures to protect your information:</p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li><strong>Encryption:</strong> All data is encrypted in transit and at rest</li>
              <li><strong>Access Controls:</strong> Strict access controls and authentication requirements</li>
              <li><strong>Regular Audits:</strong> Regular security audits and vulnerability assessments</li>
              <li><strong>Data Centres:</strong> Secure, certified data centres with physical security</li>
              <li><strong>Backup:</strong> Regular backups with secure storage</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">5. Your Rights and Choices</h2>
            <p className="text-gray-600 mb-4">Under GDPR and other applicable laws, you have the right to:</p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal information</li>
              <li><strong>Rectification:</strong> Correct inaccurate or incomplete information</li>
              <li><strong>Erasure:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Export your data in a machine-readable format</li>
              <li><strong>Restriction:</strong> Limit how we process your information</li>
              <li><strong>Objection:</strong> Object to certain types of processing</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent for processing based on consent</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">6. Data Retention</h2>
            <p className="text-gray-600 mb-6">
              We retain your information for as long as your account is active or as needed to provide our services. 
              When you cancel your account, we will delete your personal information within 30 days, except where we are 
              required to retain it for legal, regulatory, or legitimate business purposes.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">7. Cookies and Tracking Technologies</h2>
            <p className="text-gray-600 mb-4">We use cookies and similar technologies to improve your experience and operate our Service. Here's what you need to know:</p>
            
            <h3 className="text-xl font-semibold text-[#0E163B] mt-6 mb-4">What are cookies?</h3>
            <p className="text-gray-600 mb-4">
              Cookies are small text files stored on your device that help websites remember your preferences and understand how you use the site.
            </p>

            <h3 className="text-xl font-semibold text-[#0E163B] mt-6 mb-4">Types of cookies we use:</h3>
            
            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <h4 className="font-semibold text-[#0E163B] mb-2">1. Strictly Necessary Cookies (Always Active)</h4>
              <p className="text-sm text-gray-600 mb-2">
                These cookies are essential for the website to function and cannot be disabled. They enable core functionality such as:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 ml-4">
                <li>Security and authentication</li>
                <li>Network management</li>
                <li>Accessibility features</li>
                <li>Session management</li>
                <li>Load balancing</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <h4 className="font-semibold text-[#0E163B] mb-2">2. Functional Cookies (Optional)</h4>
              <p className="text-sm text-gray-600 mb-2">
                These cookies enable enhanced functionality and personalization:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 ml-4">
                <li>Remember your language and currency preferences</li>
                <li>Save your settings and preferences</li>
                <li>Provide enhanced features</li>
                <li>Remember previous actions</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <h4 className="font-semibold text-[#0E163B] mb-2">3. Analytics Cookies (Optional)</h4>
              <p className="text-sm text-gray-600 mb-2">
                These cookies help us understand how visitors interact with our website:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 ml-4">
                <li><strong>Google Analytics:</strong> Tracks page views, session duration, bounce rate</li>
                <li><strong>Performance monitoring:</strong> Helps us identify technical issues</li>
                <li><strong>User behaviour:</strong> Understand which features are most popular</li>
                <li><strong>Conversion tracking:</strong> Measure effectiveness of our marketing</li>
              </ul>
              <p className="text-sm text-gray-600 mt-2">
                Note: All analytics data is collected anonymously and cannot be used to identify you personally.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h4 className="font-semibold text-[#0E163B] mb-2">4. Marketing/Advertising Cookies (Optional)</h4>
              <p className="text-sm text-gray-600 mb-2">
                These cookies track your activity across websites to deliver relevant advertisements:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 ml-4">
                <li><strong>Google Ads:</strong> Shows relevant ads based on your interests</li>
                <li><strong>Facebook Pixel:</strong> Enables retargeting on Facebook and Instagram</li>
                <li><strong>Remarketing:</strong> Shows you relevant ads after you leave our site</li>
                <li><strong>Attribution:</strong> Measures which ads lead to conversions</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-[#0E163B] mt-6 mb-4">How to control cookies:</h3>
            <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
              <li><strong>Cookie Banner:</strong> Use our cookie consent banner to choose which cookies to accept</li>
              <li><strong>Browser Settings:</strong> Most browsers allow you to refuse cookies or delete existing ones</li>
              <li><strong>Browser Add-ons:</strong> Install privacy-focused browser extensions</li>
              <li><strong>Opt-Out Tools:</strong> Use industry opt-out tools like Network Advertising Initiative (NAI) or Digital Advertising Alliance (DAA)</li>
            </ul>
            <p className="text-gray-600 mb-6">
              <strong>Important:</strong> Disabling certain cookies may affect the functionality of our website. Necessary cookies cannot be disabled 
              as they are essential for the site to work.
            </p>

            <h3 className="text-xl font-semibold text-[#0E163B] mt-6 mb-4">Third-party cookies:</h3>
            <p className="text-gray-600 mb-4">
              Some cookies on our site are set by third-party services we use. We do not control these cookies. Please review the privacy policies 
              of these third parties to understand how they use your information:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#FF5A3D] hover:underline">Google Analytics Privacy Policy</a></li>
              <li><a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer" className="text-[#FF5A3D] hover:underline">Google Ads Privacy Policy</a></li>
              <li><a href="https://www.facebook.com/privacy/explanation" target="_blank" rel="noopener noreferrer" className="text-[#FF5A3D] hover:underline">Facebook Privacy Policy</a></li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">8. International Data Transfers</h2>
            <p className="text-gray-600 mb-6">
              Your information is primarily processed and stored in the United Kingdom. If we transfer data outside the UK/EEA, 
              we ensure appropriate safeguards are in place to protect your information in accordance with applicable data protection laws.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">9. Children's Privacy</h2>
            <p className="text-gray-600 mb-6">
              Our service is not intended for children under 16 years of age. We do not knowingly collect personal information 
              from children under 16. If you are a parent or guardian and believe your child has provided us with personal information, 
              please contact us so we can delete such information.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">10. Changes to This Privacy Policy</h2>
            <p className="text-gray-600 mb-6">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by email or 
              through our service at least 30 days before the changes take effect. Your continued use of our service after 
              the changes take effect constitutes acceptance of the updated Privacy Policy.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">11. Data Protection Officer & Legal Basis</h2>
            <p className="text-gray-600 mb-4">
              <strong>Data Controller:</strong> Local Business Heroes is the data controller responsible for your personal information.
            </p>
            <p className="text-gray-600 mb-4">
              <strong>Legal Basis for Processing:</strong> We process your personal data under the following legal bases:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li><strong>Contract Performance:</strong> Processing necessary to provide our services to you</li>
              <li><strong>Legitimate Interests:</strong> To improve our services, prevent fraud, and ensure security</li>
              <li><strong>Consent:</strong> When you've given explicit consent (e.g., marketing communications)</li>
              <li><strong>Legal Obligation:</strong> To comply with applicable laws and regulations</li>
            </ul>
            <p className="text-gray-600 mb-6">
              <strong>Supervisory Authority:</strong> You have the right to lodge a complaint with the Information Commissioner's Office (ICO) 
              in the UK or your local data protection authority if you believe your data protection rights have been violated.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">12. Automated Decision-Making</h2>
            <p className="text-gray-600 mb-6">
              We do not use automated decision-making or profiling that produces legal effects or similarly significantly affects you. 
              Any automated systems we use (such as lead scoring or recommendation engines) are designed to assist, not replace, human decision-making.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">13. Third-Party Services</h2>
            <p className="text-gray-600 mb-4">Our service integrates with the following types of third-party services:</p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li><strong>Payment Processing:</strong> Stripe (for secure payment processing)</li>
              <li><strong>Analytics:</strong> Google Analytics (for understanding service usage)</li>
              <li><strong>Communication:</strong> SMS and email service providers (for sending messages on your behalf)</li>
              <li><strong>Cloud Hosting:</strong> Secure cloud infrastructure providers</li>
            </ul>
            <p className="text-gray-600 mb-6">
              Each third-party service provider has their own privacy policy and data protection practices. We only work with providers 
              that maintain appropriate data protection standards.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">14. Your Customer Data</h2>
            <p className="text-gray-600 mb-6">
              When you use our service to manage your business, you may store data about your own customers. You are the data controller 
              for this data, and we act as a data processor. You are responsible for ensuring you have appropriate legal bases and 
              consent to collect and process your customers' data. We provide tools to help you comply with data protection laws, 
              but you remain responsible for your own compliance.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">15. Data Breach Notification</h2>
            <p className="text-gray-600 mb-6">
              In the unlikely event of a data breach that affects your personal information, we will notify you and the relevant 
              supervisory authority within 72 hours as required by GDPR. We maintain comprehensive security incident response procedures 
              to minimise any potential impact.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">16. Contact Us</h2>
            <p className="text-gray-600 mb-6">
              If you have any questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Mail size={20} className="text-[#FF5A3D]" />
                  <span className="text-gray-700">privacy@localbusinessheroes.net</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone size={20} className="text-[#FF5A3D]" />
                  <span className="text-gray-700">UK: +44 20 4634 9070</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone size={20} className="text-[#FF5A3D]" />
                  <span className="text-gray-700">USA: +1 866-540-6506</span>
                </div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                This Privacy Policy is effective as of {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} 
                and will remain in effect except with respect to any changes in its provisions in the future, which will be 
                in effect immediately after being posted on this page.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <Card className="p-8 bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] text-white">
            <CardContent className="pt-6">
              <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Your Data is Safe with Us
              </h2>
              <p className="text-xl opacity-90 mb-6">
                Now that you understand how we protect your privacy, start your 14-day trial with confidence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  asChild
                  className="bg-white text-[#FF5A3D] hover:bg-gray-100 font-semibold px-8 py-3"
                >
                  <a href="/pricing">
                    Start 14-Day Trial for {formatPrice(5)}
                  </a>
                </Button>
                <Button asChild variant="outline" className="border-white text-white hover:bg-white hover:text-[#FF5A3D]">
                  <a href="/contact">Privacy Questions? Contact Us</a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;